import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Avatar,
  Skeleton,
} from '@mui/material';
import {
  ViewList,
  ViewModule,
  Code,
  Close,
  Delete,
  Search,
  PhotoCamera,
  Inventory2,
  Person,
  CalendarToday,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useShares, useShareMessages } from '../hooks/useShares';
import { Share, ViewMode } from '../types/Share';
import { cloudKitService } from '../services/cloudkit';

type SortColumn = 'itemName' | 'itemQuantity' | 'itemSize' | 'itemSharedBy' | 'createdTimestamp';
type SortDirection = 'asc' | 'desc';

interface ShareCardProps {
  share: Share;
  messageCount: number;
  onShowRawData: (share: Share) => void;
  onDelete: (share: Share) => void;
}

function ShareCard({ share, messageCount, onShowRawData, onDelete }: ShareCardProps) {
  const formattedDate = new Date(share.createdTimestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const hasImage = !!share.itemImage;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.06)',
      }}
    >
      <Box sx={{ position: 'relative', height: 180, overflow: 'hidden' }}>
        {hasImage ? (
          <Box
            component="img"
            src={share.itemImage}
            alt={share.itemName}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 50%, #ddd6fe 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <PhotoCamera sx={{ fontSize: 40, color: '#a5b4fc', opacity: 0.7 }} />
            <Typography variant="caption" sx={{ color: '#818cf8', fontWeight: 500 }}>
              No photo
            </Typography>
          </Box>
        )}
        {/* Gradient overlay at bottom of image */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 60,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.4))',
            pointerEvents: 'none',
          }}
        />
        {/* Quantity badge */}
        <Chip
          label={`${share.itemQuantity} ${share.itemSize}`}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(8px)',
            fontWeight: 600,
            fontSize: '0.75rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        />
        {/* Name at bottom of image */}
        <Typography
          variant="h6"
          sx={{
            position: 'absolute',
            bottom: 10,
            left: 14,
            color: '#ffffff',
            fontWeight: 700,
            textShadow: '0 1px 4px rgba(0,0,0,0.3)',
            fontSize: '1.1rem',
          }}
        >
          {share.itemName.charAt(0).toUpperCase() + share.itemName.slice(1).toLowerCase()}
        </Typography>
      </Box>

      <CardContent sx={{ flexGrow: 1, pt: 1.5, pb: 0.5 }}>
        {share.itemDescription && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.5,
            }}
          >
            {share.itemDescription}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {share.itemSharedBy && (
            <Chip
              icon={<Person sx={{ fontSize: 14 }} />}
              label={share.itemSharedBy}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem', height: 26 }}
            />
          )}
          <Chip
            icon={<CalendarToday sx={{ fontSize: 12 }} />}
            label={formattedDate}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem', height: 26, color: 'text.secondary', borderColor: '#e2e8f0' }}
          />
          {messageCount > 0 && (
            <Chip
              label={`${messageCount} msg${messageCount > 1 ? 's' : ''}`}
              size="small"
              color="primary"
              sx={{ fontSize: '0.75rem', height: 26, fontWeight: 600 }}
            />
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 1.5, pt: 0.5, justifyContent: 'flex-end', gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => onShowRawData(share)}
          title="Show Raw Data"
          sx={{
            color: 'text.secondary',
            '&:hover': { backgroundColor: '#f1f5f9', color: '#6366f1' },
          }}
        >
          <Code fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onDelete(share)}
          title="Delete"
          sx={{
            color: 'text.secondary',
            '&:hover': { backgroundColor: '#fef2f2', color: '#ef4444' },
          }}
        >
          <Delete fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
}

export function ShareList() {
  const queryClient = useQueryClient();
  const { shares, isLoading, error } = useShares();
  const { messages } = useShareMessages();
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [searchTerm, setSearchTerm] = useState('');
  const [showMyShares, setShowMyShares] = useState(false);
  const [currentUserName, setCurrentUserName] = useState('');
  const [rawDataShare, setRawDataShare] = useState<Share | null>(null);
  const [deleteShare, setDeleteShare] = useState<Share | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('itemName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    setCurrentUserName(cloudKitService.getUserName());

    const handleAuthChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ authenticated: boolean; userName: string }>;
      setCurrentUserName(customEvent.detail.userName || '');
    };
    window.addEventListener('cloudkit-auth-change', handleAuthChange);
    return () => window.removeEventListener('cloudkit-auth-change', handleAuthChange);
  }, []);

  const handleShowRawData = (share: Share) => {
    setRawDataShare(share);
  };

  const handleCloseRawData = () => {
    setRawDataShare(null);
  };

  const handleDeleteClick = (share: Share) => {
    setDeleteShare(share);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteShare) return;
    setIsDeleting(true);
    try {
      await cloudKitService.deleteShare(deleteShare.recordName);
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      setDeleteShare(null);
    } catch (error) {
      console.error('Failed to delete share:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteShare(null);
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredShares = useMemo(() => {
    let result = shares;

    if (showMyShares && currentUserName) {
      const lowerUser = currentUserName.toLowerCase();
      result = result.filter(share =>
        (share.itemSharedBy || '').toLowerCase() === lowerUser
      );
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(share =>
        share.itemName.toLowerCase().includes(lowerSearch) ||
        (share.itemDescription || '').toLowerCase().includes(lowerSearch) ||
        (share.itemSharedBy || '').toLowerCase().includes(lowerSearch)
      );
    }

    return [...result].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (sortColumn === 'createdTimestamp') {
        aVal = new Date(aVal as string).getTime();
        bVal = new Date(bVal as string).getTime();
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal as string).toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [shares, searchTerm, showMyShares, currentUserName, sortColumn, sortDirection]);

  const messageCountByShare = useMemo(() => {
    const counts: Record<string, number> = {};
    messages.forEach(msg => {
      counts[msg.shareParent] = (counts[msg.shareParent] || 0) + 1;
    });
    return counts;
  }, [messages]);

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: ViewMode | null) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="rounded" width={300} height={40} />
          <Skeleton variant="rounded" width={88} height={40} />
        </Box>
        <Grid container spacing={2.5}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Skeleton variant="rounded" height={320} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading shares: {error.message}
        <Box mt={1}>
          <Typography variant="body2">
            Make sure CloudKit is configured with a valid API token.
          </Typography>
        </Box>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            {showMyShares ? 'My Shares' : 'Shared Items'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {filteredShares.length} item{filteredShares.length !== 1 ? 's' : ''}{showMyShares ? ' shared by you' : ' shared in your community'}
          </Typography>
        </Box>
        {currentUserName && (
          <ToggleButtonGroup
            value={showMyShares ? 'mine' : 'all'}
            exclusive
            onChange={(_e, val) => { if (val) setShowMyShares(val === 'mine'); }}
            size="small"
          >
            <ToggleButton value="all">
              All Shares
            </ToggleButton>
            <ToggleButton value="mine">
              My Shares
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder="Search by name, description, or person..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: { xs: '100%', sm: 360 },
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="card" aria-label="card view">
            <ViewModule />
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <ViewList />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Card View */}
      {viewMode === 'card' ? (
        <Grid container spacing={2.5}>
          {filteredShares.map((share) => (
            <Grid key={share.recordName} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <ShareCard
                share={share}
                messageCount={messageCountByShare[share.recordName] || 0}
                onShowRawData={handleShowRawData}
                onDelete={handleDeleteClick}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        /* Table View */
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table sx={{ minWidth: { xs: 500, md: 'auto' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 64, display: { xs: 'none', sm: 'table-cell' } }}>Photo</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'itemName'}
                    direction={sortColumn === 'itemName' ? sortDirection : 'asc'}
                    onClick={() => handleSort('itemName')}
                  >
                    Item Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'itemQuantity'}
                    direction={sortColumn === 'itemQuantity' ? sortDirection : 'asc'}
                    onClick={() => handleSort('itemQuantity')}
                  >
                    Qty
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <TableSortLabel
                    active={sortColumn === 'itemSize'}
                    direction={sortColumn === 'itemSize' ? sortDirection : 'asc'}
                    onClick={() => handleSort('itemSize')}
                  >
                    Size
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'itemSharedBy'}
                    direction={sortColumn === 'itemSharedBy' ? sortDirection : 'asc'}
                    onClick={() => handleSort('itemSharedBy')}
                  >
                    Shared By
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <TableSortLabel
                    active={sortColumn === 'createdTimestamp'}
                    direction={sortColumn === 'createdTimestamp' ? sortDirection : 'asc'}
                    onClick={() => handleSort('createdTimestamp')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Messages</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredShares.map((share) => (
                <TableRow key={share.recordName} hover>
                  <TableCell sx={{ p: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                    <Avatar
                      src={share.itemImage}
                      variant="rounded"
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: '#e0e7ff',
                      }}
                    >
                      <Inventory2 sx={{ fontSize: 20, color: '#818cf8' }} />
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.9rem">
                      {share.itemName.charAt(0).toUpperCase() + share.itemName.slice(1).toLowerCase()}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {share.itemDescription || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={500}>{share.itemQuantity}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Chip label={share.itemSize} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                  </TableCell>
                  <TableCell>
                    {share.itemSharedBy ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#c7d2fe', color: '#4338ca', display: { xs: 'none', sm: 'flex' } }}>
                          {share.itemSharedBy.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" noWrap>{share.itemSharedBy}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {new Date(share.createdTimestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {(messageCountByShare[share.recordName] || 0) > 0 ? (
                      <Chip
                        label={messageCountByShare[share.recordName]}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 600, minWidth: 28 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">0</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleShowRawData(share)}
                        title="Show Raw Data"
                        sx={{ '&:hover': { color: '#6366f1' } }}
                      >
                        <Code fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(share)}
                        title="Delete"
                        sx={{ '&:hover': { color: '#ef4444' } }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Empty State */}
      {filteredShares.length === 0 && !isLoading && (
        <Box
          textAlign="center"
          py={8}
          sx={{
            borderRadius: 4,
            backgroundColor: '#ffffff',
            border: '2px dashed #e2e8f0',
            mt: 2,
          }}
        >
          <Inventory2 sx={{ fontSize: 56, color: '#c7d2fe', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
            {searchTerm ? 'No matches found' : 'No shares yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm
              ? `Try a different search term`
              : 'Be the first to share something with your community!'}
          </Typography>
        </Box>
      )}

      {/* Raw Data Dialog */}
      <Dialog
        open={rawDataShare !== null}
        onClose={handleCloseRawData}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Code sx={{ color: '#6366f1' }} />
              <Typography variant="h6">
                {rawDataShare?.itemName}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseRawData} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              backgroundColor: '#1e293b',
              color: '#e2e8f0',
              p: 2.5,
              borderRadius: 2,
              overflow: 'auto',
              fontSize: '0.8rem',
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              lineHeight: 1.6,
            }}
          >
            {rawDataShare && JSON.stringify(rawDataShare, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseRawData} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteShare !== null}
        onClose={handleDeleteCancel}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete sx={{ color: '#ef4444' }} />
            Delete Share
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteShare?.itemName}"</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={isDeleting} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
